/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import { useTranslation } from 'react-i18next';
import { Grid, useMediaQuery, useTheme } from '@material-ui/core';

import { GoalType, ICustomTheme } from 'interfaces';
import { CustomButton } from 'components';
import { GOAL } from 'constants/challenges';
import * as S from './styles';

type ConditionsModalType = {
  additionalInformation: string;
  calculationRules?: string[];
  conditions?: string[];
  eligibilityRequirements?: string[];
  goalType: GoalType;
  open?: boolean;
  participantsProfiles?: string[];
  setOpenModal: (value: boolean) => void;
};

const cssAlignLeft = css({ textAlign: 'left' });

const cssMarginBottom2 = (theme: ICustomTheme) =>
  css({
    marginBottom: theme.spacing(2),
  });

const ConditionsModal = ({
  additionalInformation,
  calculationRules,
  conditions,
  eligibilityRequirements,
  goalType,
  open = false,
  participantsProfiles,
  setOpenModal,
}: ConditionsModalType): JSX.Element | null => {
  const theme: ICustomTheme = useTheme();
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const customConditions: [string[] | undefined, string][] = [
    [calculationRules, 'challenges.detail.goals_and_rewards.conditions.rules'],
    [
      participantsProfiles,
      'challenges.detail.goals_and_rewards.conditions.profiles',
    ],
    [
      eligibilityRequirements,
      'challenges.detail.goals_and_rewards.conditions.eligibilities',
    ],
  ];

  return (
    <S.StyledModal open={open} onClose={() => setOpenModal(false)}>
      <S.Container>
        <S.Title variant={isDownSm ? 'body1' : 'h3'}>
          {t('challenges.detail.goals_and_rewards.conditions.title')}
        </S.Title>

        <Grid container spacing={isDownSm ? 5 : 7} css={cssMarginBottom2}>
          {goalType === GOAL.GENERIC &&
            conditions?.map((condition: string) => (
              <Grid item css={cssAlignLeft}>
                <S.Condition
                  variant={isDownSm ? 'body2' : 'body1'}
                  key={condition}
                >
                  {condition}
                </S.Condition>
              </Grid>
            ))}

          {goalType === GOAL.CUSTOM &&
            customConditions.map(([data, text]) =>
              data?.length ? (
                <Grid item xs css={cssAlignLeft} key={text}>
                  <S.Subtitle variant={isDownSm ? 'body2' : 'h4'} paragraph>
                    {t(text)}
                  </S.Subtitle>
                  {data.map((rule: string) => (
                    <S.Condition
                      variant={isDownSm ? 'body2' : 'body1'}
                      key={rule}
                    >
                      {rule}
                    </S.Condition>
                  ))}
                </Grid>
              ) : null,
            )}

          {goalType === GOAL.BY_PROFILE && additionalInformation.length ? (
            <Grid item css={cssAlignLeft}>
              <S.Condition variant={isDownSm ? 'body2' : 'body1'}>
                {additionalInformation}
              </S.Condition>
            </Grid>
          ) : null}
        </Grid>

        <CustomButton
          btnType="button"
          color="secondaryInverted"
          size={isDownSm ? 'sm' : 'md'}
          onClick={() => setOpenModal(false)}
        >
          {t('core.close')}
        </CustomButton>
      </S.Container>
    </S.StyledModal>
  );
};

export default ConditionsModal;
